import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { ProductChangeConnectionsApi } from "src/api/generated/erp/productChanges/api/productChangeConnectionsApi.ts";
import { ProductChangeRequestView } from "src/api/generated/erp/db/types/tables/productChangeRequestView.ts";
import { enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getProductChangeRequestStateLabels } from "src/api/generated/erp/db/types/enums/productChangeRequestState.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ConnectRequestsToProductChangeDialog } from "src/components/views/erp/productChanges/changes/linkedRequests/ConnectRequestsToProductChangeDialog.tsx";
import { FormCrudDataGrid } from "../../../../../common/dataGrid/crud/FormCrudDataGrid.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProductChangeLinkedRequestsTabProps {
	productChangeId: number;
}

export const ProductChangeLinkedRequestsTab = ({ productChangeId }: ProductChangeLinkedRequestsTabProps) => {
	const { openDialog } = useGenericDialog();

	return (
		<ClientSideDataGridModel
			gridId={"C6D9D6BC2B2D5A0F"}
			initialParams={{}}
			fetchData={() => ProductChangeConnectionsApi.getConnectedRequests({ productChangeId })}
			getRows={(data) => data}
			getRowId={(row) => row.productChangeRequestId}
			customFunctionUiActionView={"PRODUCT_CHANGE_REQUESTS"}
			render={({ dataGridProps, refreshData }) => (
				<FormCrudDataGrid<ProductChangeRequestView>
					columns={[
						integerColumn({
							field: "productChangeRequestId",
							headerName: i18n.t("id"),
							width: 90,
						}),
						textColumn({
							field: "title",
							headerName: i18n.t("title"),
							width: 260,
						}),
						textColumn({
							field: "categoryName",
							headerName: i18n.t("category"),
							width: 160,
						}),
						enumColumn({
							field: "state",
							headerName: i18n.t("state"),
							enumLabels: getProductChangeRequestStateLabels(),
							width: 200,
						}),
					]}
					actionBarComponents={
						<AsyncButton
							icon={faLink}
							label={i18n.t("connect")}
							variant={"outlined"}
							onClick={() => {
								openDialog(({ closeDialog }) => ({
									title: i18n.t("connect"),
									size: "lg",
									agentContext: {
										key: defineAgentUiContextKey(
											"erp.productChanges.connectRequestsToProductChange.dialog",
										),
										data: { productChangeId },
										pathSegments: [i18n.t("connect")],
									},
									content: (
										<ConnectRequestsToProductChangeDialog
											productChangeId={productChangeId}
											onCompleted={async () => {
												closeDialog();
												await refreshData();
											}}
										/>
									),
								}));
							}}
						/>
					}
					remove={{
						type: "enabled",
						action: async ({ items }) => {
							await ProductChangeConnectionsApi.disconnectRequests({
								productChangeId,
								productChangeRequestIds: items.map((item) => item.productChangeRequestId),
							});
						},
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.productChanges.linkedRequests.grid"),
						data: { productChangeId },
						rowFields: ["productChangeRequestId", "productChangeId", "categoryId", "state"],
						selectedRowPathSegment: (row) => `${i18n.t("request")} ${row.productChangeRequestId}`,
						selectedRowsPathSegment: (count) => i18n.t("product_change_requests_selected_count", { count }),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
